import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ConciergeCard from "../components/ConciergeCard";
import ExperienceCard from "../components/ExperienceCard";
import MapView from "../components/MapView";
import { FALLBACK_CONCIERGES } from "../lib/fallbacks";
import { Search, Sparkles, Heart, Plane, Gem, Briefcase, Utensils, Music, Users as UsersIcon, Cake, Globe2, ArrowRight } from "lucide-react";

const CITIES = ["Miami","Orlando","New York","Los Angeles","Las Vegas","Washington","Nashville","Austin","Chicago","Denver","Scottsdale","Charleston"];

// TREL is an AI-powered entertainment + lifestyle concierge marketplace.
// Phase 44 — refreshed category set per the "Browse Concierges should feel
// like Airbnb discovery, not a service directory" mandate.
const TREL_CATEGORIES = [
  { key: "travel",        label: "Travel",                sub: "Vacations · Trips · Lifestyle",       icon: Plane,     accent: "#064E3B" },
  { key: "dining",        label: "Dining",                sub: "Private chef · Restaurants · Wine",   icon: Utensils,  accent: "#FF7A45" },
  { key: "events",        label: "Events",                sub: "Celebrations · Parties · Surprises",  icon: Sparkles,  accent: "#00AEEF" },
  { key: "entertainment", label: "Nightlife",             sub: "VIP · Bottle service · Tables",       icon: Music,     accent: "#071A2D" },
  { key: "luxury",        label: "Luxury",                sub: "Premium access · Yachts · Estate",    icon: Gem,       accent: "#C9A45C" },
  { key: "family",        label: "Family",                sub: "Kids-friendly · Multi-gen · Travel",  icon: UsersIcon, accent: "#064E3B" },
  { key: "corporate",     label: "Corporate",             sub: "Retreats · Client · Team events",     icon: Briefcase, accent: "#071A2D" },
  { key: "moments",       label: "Special Occasions",     sub: "Anniversaries · Proposals · Birthdays", icon: Cake,    accent: "#FF7A45" },
];

// Sort verified concierges by trust tier (Elite > Certified > Identity > rest)
function trustScore(p) {
  let s = 0;
  if (p?.elite_badge_active) s += 8;
  if (p?.trel_certified || (p?.certification && p.certification.completed_at)) s += 4;
  if (p?.identity_verified) s += 2;
  if (p?.trel_plus_active) s += 1;
  s += Math.min(2, (p?.rating || 0) / 5 * 2);
  return s;
}

export default function Explore(){
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [concierges, setConcierges] = useState(FALLBACK_CONCIERGES);
  const [city, setCity] = useState(params.get("city")||"");
  const [moment, setMoment] = useState(params.get("moment")||"");
  const [category, setCategory] = useState(params.get("category")||"");
  const [eliteOnly, setEliteOnly] = useState(params.get("elite") === "1");
  const [q, setQ] = useState("");

  // Concierges — the primary focus of Explore (TREL is a concierge marketplace).
  useEffect(()=>{
    const url = `/providers?verified=true${city?`&city=${city}`:""}${eliteOnly?`&elite_only=true`:""}`;
    api.get(url)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        const ranked = [...list].sort((a, b) => trustScore(b) - trustScore(a));
        // Dedupe by name+city — older seed runs leave ghost rows
        const seen = new Set();
        const unique = ranked.filter((p) => {
          const k = `${p.name}|${p.city}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        }).map(p => ({
          ...p,
          title: p.title || (
            (p.categories || []).includes("entertainment") ? "VIP & Entertainment Concierge"
              : (p.categories || []).includes("travel") ? "Travel & Lifestyle Concierge"
              : (p.categories || []).includes("corporate") ? "Corporate Events Concierge"
              : "Experience Concierge"
          ),
          starting_at: p.price_floor || 49,
          moments_created: p.moments_created || Math.max(20, Math.round((p.earnings || 0) / 250)),
          specializes: p.specializes || p.categories || ["Birthdays", "VIP", "Corporate Events", "Celebrations"],
          photo: p.photo || p.profile_photo,
        }));
        setConcierges(unique.length > 0 ? unique : FALLBACK_CONCIERGES);
      })
      .catch(() => setConcierges(FALLBACK_CONCIERGES));
  },[city, eliteOnly]);

  // Example experiences — shown as inspiration, NOT as the primary marketplace
  useEffect(()=>{
    const url = `/experiences?${city?`city=${city}&`:""}${moment?`moment=${moment}&`:""}${category?`category=${category}&`:""}${eliteOnly?`elite_only=true&`:""}${q?`q=${q}`:""}`;
    api.get(url).then(r=>setItems(Array.isArray(r.data) ? r.data : [])).catch(()=>setItems([]));
  },[city, moment, category, eliteOnly, q]);

  const setCategoryFilter = (key) => {
    const next = category === key ? "" : key;
    setCategory(next);
    const sp = new URLSearchParams(params);
    if (next) sp.set("category", next); else sp.delete("category");
    setParams(sp, { replace: true });
  };

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="text-xs uppercase tracking-[0.22em] text-[#064E3B]" data-testid="explore-kicker">Browse Concierges</div>
      <h1 className="font-serif text-4xl md:text-5xl mt-2 text-[#071A2D]" data-testid="explore-headline">
        Meet the experts behind every <span className="italic text-[#064E3B]">unforgettable moment.</span>
      </h1>
      <p className="text-[#071A2D]/70 mt-4 max-w-2xl leading-relaxed">
        Discover trusted TREL Concierges — handpicked, verified, and ready to plan, coordinate, and deliver complete experiences. Pick one yourself, or let TREL AI match you.
      </p>

      {/* AI matching banner — fast path for indecisive customers */}
      <div
        data-testid="explore-ai-banner"
        className="mt-6 rounded-3xl bg-gradient-to-r from-[#00AEEF]/8 via-white to-[#FFF8EE] border border-[#00AEEF]/25 px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
      >
        <div className="flex items-start sm:items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#00AEEF]/10 text-[#00AEEF] flex items-center justify-center shrink-0">
            <Sparkles size={18} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#00AEEF]">Not sure who to choose?</div>
            <p className="mt-0.5 text-[#071A2D] font-medium leading-snug">Let TREL AI match you with the right pro.</p>
          </div>
        </div>
        <Link
          to="/ai"
          data-testid="explore-ai-match"
          className="btn-coral inline-flex items-center gap-2 shrink-0 self-start sm:self-auto"
        >
          Get matched <ArrowRight size={14} />
        </Link>
      </div>

      {/* Remote vs Local — clear that concierges aren't bound by geography */}
      <div className="mt-5 flex items-start gap-3 text-[13px] text-[#071A2D]/65" data-testid="explore-remote-local">
        <Globe2 size={16} className="mt-0.5 text-[#064E3B] shrink-0" />
        <p>
          <strong className="text-[#071A2D]">U.S.-based Concierges</strong> handle both in-person service (when location matches) <em>and</em> remote coordination for international trips, vendor management, and travel planning anywhere in the world.
        </p>
      </div>

      {/* Category pills — premium discovery, 8 customer-goal categories */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {TREL_CATEGORIES.map((c) => {
          const active = category === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategoryFilter(c.key)}
              data-testid={`category-${c.key}`}
              className={`rounded-2xl text-left p-4 border transition shadow-[0_1px_2px_rgba(28,25,23,0.04)] hover:shadow-[0_8px_20px_-8px_rgba(28,25,23,0.16)] ${
                active ? "bg-[#FFF8EE] border-[#064E3B]/30" : "bg-white border-stone-200 hover:border-[#071A2D]/30"
              }`}
              style={active ? { boxShadow: `0 0 0 2px ${c.accent}22 inset` } : undefined}
            >
              <c.icon size={18} style={{ color: c.accent }} />
              <div className="font-medium text-sm mt-2 text-[#071A2D]">{c.label}</div>
              <div className="text-[11px] text-[#071A2D]/55 mt-1 leading-snug">{c.sub}</div>
            </button>
          );
        })}
      </div>

      {/* Filters — concierge-focused, not activity-focused */}
      <div className="flex flex-wrap gap-3 mt-7">
        <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-[#FFF8EE] rounded-full px-4 py-2 border border-[#071A2D]/10">
          <Search size={16} className="text-[#064E3B]"/>
          <input data-testid="search-input" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search concierges & moments" className="bg-transparent outline-none w-full"/>
        </div>
        <select data-testid="city-filter" value={city} onChange={e=>setCity(e.target.value)} className="rounded-full px-4 py-2 bg-[#FFF8EE] border border-[#071A2D]/10">
          <option value="">All cities</option>
          {CITIES.map(c=><option key={c}>{c}</option>)}
        </select>
        <select data-testid="moment-filter" value={moment} onChange={e=>setMoment(e.target.value)} className="rounded-full px-4 py-2 bg-[#FFF8EE] border border-[#071A2D]/10">
          <option value="">All moments</option>
          {["romantic","birthday","proposal","vip","family","luxury","wellness","date","bucket","hidden","adventure"].map(m=><option key={m}>{m}</option>)}
        </select>
        <button
          type="button"
          data-testid="elite-only-toggle"
          onClick={() => {
            const next = !eliteOnly;
            setEliteOnly(next);
            const sp = new URLSearchParams(params);
            if (next) sp.set("elite", "1"); else sp.delete("elite");
            setParams(sp, { replace: true });
          }}
          className={`rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 transition ${
            eliteOnly
              ? "bg-gradient-to-r from-[#C9A45C] to-[#E0BE7A] text-[#071A2D] border border-[#C9A45C]"
              : "bg-[#FFF8EE] border border-[#071A2D]/15 text-[#071A2D] hover:bg-[#C9A45C]/10"
          }`}
          title="Show only TREL Elite Concierges — recommended for corporate, luxury, and VIP experiences"
        >
          <Gem size={14}/> {eliteOnly ? "Showing Elite only" : "Show Elite only"}
        </button>
      </div>

      {/* CONCIERGES — primary content of Explore */}
      <div className="mt-10">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">Trusted TREL Concierges</div>
            <h2 className="font-serif text-3xl mt-1">Choose the expert who creates your moment.</h2>
          </div>
          <Link to="/ai" className="text-sm underline whitespace-nowrap text-[#064E3B]">Or let AI match me →</Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="concierges-grid">
          {concierges.slice(0, 9).map(c => <ConciergeCard key={c.user_id} c={c}/>)}
        </div>
        {concierges.length === 0 && (
          <div className="p-10 rounded-3xl bg-[#FFF8EE] border border-[#071A2D]/10 text-center">
            <div className="font-serif text-2xl">No Concierges match those filters yet</div>
            <div className="text-sm text-[#071A2D]/60 mt-2">
              Tell our AI Concierge what you want — we&apos;ll match you with a trusted Experience Concierge.
              <Link to="/ai" className="underline ml-2 text-[#064E3B]">Start with AI →</Link>
            </div>
          </div>
        )}
      </div>

      {/* INSPIRATION — example experiences from concierge portfolios. Clearly framed as
          "inspiration, not menu items". Map view kept for spatial discovery. */}
      {items.length > 0 && (
        <div className="mt-16">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">Example moments · Inspiration</div>
              <h2 className="font-serif text-3xl mt-1">Moments TREL Concierges have created.</h2>
              <p className="text-sm text-[#071A2D]/60 mt-2 max-w-xl">
                Not products. Browse for inspiration — your Concierge designs the actual experience around your vision.
              </p>
            </div>
          </div>
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {items.slice(0, 6).map(e=><ExperienceCard key={e.experience_id} exp={e}/>)}
            </div>
            <div className="lg:col-span-5 lg:sticky lg:top-24 h-[500px] rounded-3xl overflow-hidden shadow-luxury">
              <MapView items={items} height="100%"/>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
